import React from "react";
import {
    Grid,
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    Box,
    Chip,
    Divider,
    IconButton,
    Tooltip,
    Button,
} from "@mui/material";

// Components
import Icon from "@/theme/vectors/bundle";

// Utils
import { getMediaTypeIcon, formatFileSize } from "@/utils/products/mediaHelpers";

const MediaGrid = ({ 
    medias, 
    activeVariantId = null,
    variants = [], 
    onSelectMediaForVariant = null,
    onOpenDeleteModal,
    isSelectionMode = false,
    t 
}) => {
    // Check if media is already selected for the active variant
    const isMediaSelected = (mediaId) => {
        if (!activeVariantId || !variants.length) return false;
        
        // Find the active variant
        const activeVariant = variants.find(v => v.id === activeVariantId);
        
        // Check if this media is in the variant's mediaIds array
        return activeVariant && 
               activeVariant.mediaIds && 
               activeVariant.mediaIds.includes(mediaId);
    };
    
    return (
        <Grid container spacing={2}>
            {medias.map((media) => {
                // Determine if this media is selected
                const selected = isSelectionMode && isMediaSelected(media.id);
                
                return (
                <Grid item xs={12} sm={6} md={4} key={media.id}>
                    <Card 
                        sx={{ 
                            height: '100%', 
                            display: 'flex', 
                            flexDirection: 'column',
                            position: 'relative',
                            '&:hover .delete-button': {
                                opacity: 1
                            },
                            border: selected ? '3px solid' : '1px solid',
                            borderColor: selected ? '#F4C430' : 'divider',
                            cursor: isSelectionMode ? 'pointer' : 'default',
                            '&:hover': {
                                borderColor: isSelectionMode ? (selected ? '#E6B800' : 'primary.light') : 'divider',
                                boxShadow: isSelectionMode ? 2 : 0
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                        onClick={isSelectionMode ? () => onSelectMediaForVariant(media.id) : undefined}
                    >
                        {/* Selection indicator - Dot in top right corner with RESYS gold color */}
                        {selected && (
                            <Box 
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: 16,
                                    height: 16,
                                    borderRadius: '50%',
                                    backgroundColor: '#F4C430',
                                    zIndex: 2,
                                    border: '2px solid',
                                    borderColor: '#FFDF80'
                                }}
                            />
                        )}
                        
                        {/* Delete Button - Top Right (only shown in non-selection mode) */}
                        {!isSelectionMode && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOpenDeleteModal(media.id);
                                }}
                                color="error"
                                size="small"
                                className="delete-button"
                                sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    zIndex: 2,
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                    }
                                }}
                            >
                                <Icon name="remove" size={18} />
                            </IconButton>
                        )}
                        
                        {media.type === 'image' ? (
                            <CardMedia
                                component="img"
                                height="140"
                                image={media.url}
                                alt={media.filename}
                            />
                        ) : (
                            <Box sx={{ 
                                height: 140, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                backgroundColor: '#f5f5f5'
                            }}>
                                {getMediaTypeIcon(media.type)}
                            </Box>
                        )}
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="body2" noWrap title={media.filename}>
                                {media.filename}
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Chip 
                                    size="small" 
                                    label={media.type} 
                                    icon={getMediaTypeIcon(media.type)} 
                                />
                                {media.filesize && (
                                    <Chip 
                                        size="small" 
                                        label={formatFileSize(media.filesize)} 
                                    />
                                )}
                            </Box>
                        </CardContent>
                        {!isSelectionMode && (
                            <>
                                <Divider />
                                <CardActions sx={{ justifyContent: 'space-between' }}>
                                    <Tooltip title="Copy URL">
                                        <IconButton
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigator.clipboard.writeText(media.url);
                                            }}
                                            size="small"
                                        >
                                            <Icon name="copy" size={18} />
                                        </IconButton>
                                    </Tooltip>
                                    <Button 
                                        size="small" 
                                        href={media.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        endIcon={<Icon name="view" size={18} />}
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {t('product.detail.view')}
                                    </Button>
                                </CardActions>
                            </>
                        )}
                    </Card>
                </Grid>
            )})}
        </Grid>
    );
};

export default MediaGrid;